import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { Assignment } from '../../../api/app-types/assignment';
import { Client } from '../../../api/app-types/client.app';
import { ContactType } from '../../../api/app-types/contact-type';
import {
	Contact,
	ContactParticipant,
	ContactValidDateRange,
	UpdateContactBody,
} from '../../../api/app-types/contact.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../api/app-types/document.app';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { SearchResultItem } from '../../../api/app-types/search.app';
import { Task } from '../../../api/app-types/task.app';
import { User } from '../../../api/app-types/user.app';
import { CONTACT_API } from '../../../api/services/contact.service';
import { UPLOAD_API, UploadApi } from '../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';
import { SearchableTaskPicker } from '../../../core/components/searchable-task-picker/searchable-task-picker';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { STATE } from '../../../core/services/state.service';
import { DocumentUploadControl } from '../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../shared/document-upload/document-upload-validators';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import {
	formatDateToISOString,
	matDateProviders,
	normalizeToEndOfDay,
	normalizeToStartOfDay,
	parseDate,
} from '../../../shared/helpers/date.func';
import { hasError } from '../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../shared/validators/max-date-validator';
import { Participants } from '../participants/participants';

@Component({
	selector: 'lvr-contact-edit',
	imports: [
		LvrHeading,
		TranslocoPipe,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatDatepickerModule,
		LvrSearchableSelect,
		MatCheckboxModule,
		LvrInputDirective,
		LvrField,
		LvrAutoSaveDirective,
		MatInputModule,
		LvrAppendAsteriskPipe,
		DatePipe,
		LvrPaddingDirective,
		LvrRestoreActions,
		DocumentUploadControl,
		Participants,
		SearchableTaskPicker,
		LvrDefaultActions,
	],
	providers: [
		matDateProviders,
		{
			provide: UPLOAD_API,
			useClass: UploadApi,
		},
	],
	templateUrl: './contact-edit.html',
	styleUrl: './contact-edit.scss',
})
export class ContactEdit extends RouteInjection {
	public readonly compareById = compareById;
	public readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CONTACT_API);
	private readonly ntfySvc = inject(NOTIFICATION_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	public readonly subjectType = DocumentFileSubjectType.CONTACT;

	private readonly contactResponse = this.resolveDataAsSignal<ApiResponse<Contact, Metadata>>('contact')!;
	private readonly contact = computed(() => this.contactResponse()?.data);
	private readonly assignment = this.resolveDataAsSignal<Assignment>('assignment');
	private readonly resolvedTypeOfCareId = this.resolveDataAsSignal<number | undefined>('typeOfCareId');
	private readonly contactTypePage = this.resolveDataAsSignal<PaginatedResponse<ContactType>>('contactTypes');
	public readonly contactTypes = computed(() => {
		const page = this.contactTypePage() as PaginatedResponse<ContactType> | ContactType[] | undefined;
		return Array.isArray(page) ? page : page?.entities;
	});
	public readonly dateRange = this.resolveDataAsSignal<ContactValidDateRange>('dateRange');
	public readonly user = this.resolveDataAsSignal<User>('user');
	public readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly clientFullName = computed(() => `${this.client().firstName} ${this.client().name}`);
	public readonly isLoading = signal<boolean>(false);

	public readonly draftId = computed(() => {
		return `${FORM_KEYS.UPDATE_CONTACT}.${this.contact()!.id}`;
	});
	public readonly canEditContactsPermission = hasPermission(this.session.permissions, [
		'Contacts.Edit',
		'Contacts.Edit.PhaseOneOnly',
	]);
	public readonly canManageContactDocuments = hasPermission(this.session.permissions, 'Documents.Contact.Edit');
	public readonly canReadTasks = hasPermission(this.session.permissions, 'Tasks.Read');
	public readonly canEditTasks = hasPermission(this.session.permissions, 'Tasks.Edit');

	private readonly participantsRef = viewChild(Participants);
	/**
	 * True while the "client participated" toggle is still fetching the client via global search.
	 * Saving must be blocked until this settles, otherwise the client can be missing from
	 * `participants` even though the checkbox appears checked.
	 */
	public readonly isClientLookupPending = computed(() => this.participantsRef()?.isClientLookupPending() ?? false);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly keywordCtrlMaxLength = this.env.textInputMaxLength;
	public readonly dateWarningRange = {
		min: this.env.formConfiguration.contact.dateWarningRange.min,
		max: this.env.formConfiguration.contact.dateWarningRange.max, // days retroactively
	};

	public readonly phaseId = signal<number>(0);
	public readonly typeOfCareId = signal<number | null>(null);
	private readonly isAdditionalAssignment = signal<boolean>(false);
	public readonly isTasksPhase = computed(() => this.isAdditionalAssignment() || this.isP2orP3(this.phaseId()));

	public readonly minDate = computed(() => {
		const dateRangeMin = this.dateRange()?.minStartDate;
		const minDateParsed = parseDate(dateRangeMin);
		return minDateParsed ? normalizeToStartOfDay(minDateParsed) : undefined;
	});

	public readonly maxDate = computed(() => {
		const date = this.dateRange()?.maxEndDate;
		if (!date) return undefined;

		return normalizeToEndOfDay(date);
	});

	public readonly form = this.initForm(inject(FormBuilder));
	public readonly contactTypeSelectControl = new FormControl<number | undefined>(undefined, {
		nonNullable: true,
		validators: [Validators.required],
	});
	public readonly contactTypeOptionValue = (type: ContactType): number => type.id;
	public readonly contactTypeOptionLabel = (type: ContactType): string => type.remark ?? '';

	public constructor() {
		super();

		const contactTypeControl = this.form.controls.contactType;
		contactTypeControl.valueChanges.subscribe((type) => {
			const typeId = type?.id;
			if (this.contactTypeSelectControl.value !== typeId) {
				this.contactTypeSelectControl.setValue(typeId, { emitEvent: false });
			}
		});
		this.contactTypeSelectControl.valueChanges.subscribe((typeId) => {
			const selectedType = typeId === undefined ? undefined : this.contactTypes()?.find((type) => type.id === typeId);
			if (contactTypeControl.value?.id !== typeId) {
				contactTypeControl.setValue(selectedType, { emitEvent: false });
			}
		});
		this.contactTypeSelectControl.setValue(contactTypeControl.value?.id, { emitEvent: false });
		effect(() => {
			const a = this.assignment();
			if (!a) return;
			const currentTypeOfCareId = a.typeOfCareId ?? null;
			const tasksTypeOfCareId = a.isAdditionalAssignment
				? (this.resolvedTypeOfCareId() ?? currentTypeOfCareId)
				: (currentTypeOfCareId ?? this.resolvedTypeOfCareId() ?? null);

			// Additional assignments uses P3 for tasks in assignments
			const pId = a.isAdditionalAssignment ? DB_PHASES.P3 : a.phaseId;
			this.phaseId.set(pId);
			this.typeOfCareId.set(tasksTypeOfCareId);
			this.isAdditionalAssignment.set(!!a.isAdditionalAssignment);
		});

		// Enables or disables the tasks picker according to the Phase
		effect(() => {
			const allowed = this.isTasksPhase() && this.canReadTasks() && this.canEditTasks();
			const ctrl = this.form.controls.selectedTasksIds;
			if (allowed) {
				ctrl.enable({ emitEvent: false });
			} else {
				ctrl.setValue([], { emitEvent: false });
				ctrl.disable({ emitEvent: false });
			}
		});
	}

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	/**
	 * Cancel new contact creation.
	 */
	public cancel = async () => {
		this.draftService.deleteDraft('contact', this.draftId());
		await this.router.navigate(['../'], { relativeTo: this.route });
	};

	/**
	 * Checks if documents form control has a specific validation error and has been touched.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if any error exists and the control was touched.
	 */
	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	public update = async () => {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		// Guards against Enter/submit or a double click bypassing the disabled save button
		// while the client lookup triggered by the participants toggle is still in flight.
		if (this.isClientLookupPending()) {
			return;
		}

		const fv = this.form.value;
		const docs = fv.documents! as DocumentFile[];

		const body: UpdateContactBody = {
			id: this.contact()!.id,
			anonym: fv.anonym ?? false,
			confidential: fv.confidential ?? false,
			contactedAt: formatDateToISOString(fv.contactedAt) ?? '',
			contactType: fv.contactType!,
			keyword: fv.keyword?.trim() ?? '',
			remark: fv.remark?.trim() ?? '',
			participants: (fv.participants ?? []).map((p: SearchResultItem) => ({
				id: p.item.id,
				typeId: p.personType,
			})),
			documentIds: docs.map((d: DocumentFile) => d.id),
			selectedTasksIds: fv.selectedTasksIds ?? [],
		};

		this.isLoading.set(true);

		try {
			await this.api.update(this.client().id, this.contact()!.id, body);
			await this.state.updateAssignments();
			this.ntfySvc.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.contact_edited')),
			);
			this.cancel();
		} catch {
			this.ntfySvc.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.contact_edit_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	};

	/**
	 * Initializes the form for editing a contact.
	 */
	private initForm(fb: FormBuilder) {
		const contact = this.contact();
		const participants = (contact?.participants ?? []).map(
			(p: ContactParticipant): SearchResultItem => ({
				item: {
					id: p.item.id,
					name: p.item.name,
					firstName: p.item.firstName,
					objectId: '',
				},
				personType: p.personTypeId,
				contactPoints: p.contactPoints,
				institutionBranch: p.institutionBranch,
			}),
		);
		const tasksIds = (contact?.tasks ?? []).map((t: Task) => t.id);

		return fb.group({
			anonym: fb.control<boolean>(contact?.anonym ?? false),
			remark: fb.control<string>(contact?.remark ?? '', [Validators.maxLength(this.remarkCtrlMaxLength)]),
			confidential: fb.control<boolean>(contact?.confidential ?? false),
			keyword: fb.control<string>(contact?.keyword ?? '', [Validators.required]),
			contactType: fb.control<ContactType | undefined>(contact?.contactType, [Validators.required]),
			contactedAt: fb.control<Date | undefined>(contact?.contactedAt, [
				Validators.required,
				minDateValidator(this.minDate()),
				maxDateValidator(this.maxDate()),
			]),
			participants: fb.control<SearchResultItem[]>(participants),
			documents: fb.control<DocumentFile[]>(contact?.documents ?? [], [
				maxFiles(this.env.contactSettings.maxFiles),
				maxFileSize(this.env.fileSettings.maxFileSize),
				allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
			]),
			selectedTasksIds: fb.control<number[]>(tasksIds),
		});
	}

	private isP2orP3 = (p?: number | null) => p === DB_PHASES.P2 || p === DB_PHASES.P3;
}
